import React              from 'react';
import View               from '@widesk-ui/components/View';
import currentEditorStore from '@/TokamapEditor/stores/currentEditorStore';
import AreaPlaceSection   from '@/TokamapEditor/ux/AreaSidebar/AreaPlaceSection';
import AreaUrlSection     from '@/TokamapEditor/ux/AreaSidebar/AreaUrlSection';
import AreaMapSection     from '@/TokamapEditor/ux/AreaSidebar/AreaMapSection';
import Choice             from '@/TokamapEditor/ux/AreaSidebar/Choice';
import { commonValue }    from '@/TokamapEditor/ux/AreaSidebar/multiSelectHelpers';
import { ExtraAction }    from '@models/map/FeatureModel';
import { observer }       from 'mobx-react-lite';
import useDesignTokens    from '@/hooks/useDesignTokens';
import FunctionalityGate  from '@/components/Functionality';
import unlinkPlaceContent from '@/TokamapEditor/helpers/unlinkPlaceContent';
import asSingleHistoryEntry from '@/TokamapEditor/helpers/asSingleHistoryEntry';
import shapePlaceId       from '@/TokamapEditor/helpers/shapePlaceId';
import { placeLabel }     from '@/TokamapEditor/helpers/placeLinkedContent';
import PlaceModel         from '@models/map/PlaceModel';

const AreaActionSection = observer(() => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;
	const tokens = useDesignTokens();

	if (!shapes.length) return null;

	const commonAction = commonValue(shapes, s => s.action);
	// `commonAction === undefined` peut aussi bien dire "action mixte" que "aucune action définie
	// nulle part" (cas courant, single ou multi) — seul `Set` distingue vraiment les deux.
	const isMixed = shapes.length > 1 && new Set(shapes.map(s => s.action)).size > 1;

	const withMapNestingGate = (choice: React.ReactNode) => (!isMixed && commonAction === 'open_map')
		? choice
		: <FunctionalityGate reference="map_nesting" fullWidth>{choice}</FunctionalityGate>;

	// Seul point d'entrée qui change `Shape.action` (`AreaPlaceSection`/`AreaUrlSection` ne font que
	// compléter la configuration d'une action déjà choisie ici) — c'est donc ici, et ici seulement,
	// qu'on vide la configuration de l'action désormais inactive. Sans ça, un consommateur qui lit
	// `shape.entity`/`shape.urlAction` sans vérifier `shape.action` (ex. `PreviewModal.tsx`) verrait
	// une configuration périmée comme toujours active après un changement d'action.
	const setAction = (value?: ExtraAction) => asSingleHistoryEntry(async () => {
		const ids = shapes.map(s => s.id);

		editor.setShapeAction(ids, value);

		if (value !== 'open_place') {
			const shapesWithEntity = shapes.filter(s => s.entity !== undefined);

			if (shapesWithEntity.length) {
				await unlinkPlaceContent(shapesWithEntity);
				editor.setShapeEntity(shapesWithEntity.map(s => s.id), undefined);
			}
		}

		if (value !== 'open_url') {
			const idsWithUrlAction = shapes.filter(s => s.urlAction !== undefined).map(s => s.id);

			if (idsWithUrlAction.length) editor.setShapeUrlAction(idsWithUrlAction, undefined);
		}

		if (value !== 'open_map') {
			const idsWithTargetMap = shapes.filter(s => s.targetDocumentId !== undefined).map(s => s.id);

			if (idsWithTargetMap.length) editor.setShapeTargetDocumentId(idsWithTargetMap, undefined);
		}
	});

	const single = !currentEditorStore.isMultiSelection ? shapes[0] : undefined;
	const linkedShape = single && shapePlaceId(single) ? single : undefined;
	const placeName = PlaceModel.localize('default', 'l');

	return (
		<View gap="md" lineHeight={1.5}>
			<View size={13} style={{ fontWeight: 600 }}>Au clic sur la forme</View>

			{isMixed && (
				<View size={12} color={tokens.text.muted}>
					Valeurs différentes selon les formes sélectionnées.
				</View>
			)}

			<View gap="xs">
				{/* La seule des trois à porter une explication. « Point d'intérêt » est le nom de l'entité
				    dans tout le produit, mais cette liste est souvent l'endroit où on le rencontre EN
				    PREMIER : sur un projet neuf la liste des points d'intérêt est vide, et la phrase
				    d'`AreaPlaceSection` qui l'explique n'apparaît qu'une fois l'option choisie — après la
				    décision qu'elle devait éclairer. On ne définit pas le terme, on dit ce que verra le
				    visiteur : ça glose le mot et donne du même coup la conséquence qui manque au libellé.
				    Le contenu décrit le vrai panneau (voir `PlacePanelPreview`). */}
				<Choice
					selected={!isMixed && commonAction === 'open_place'}
					onClick={() => setAction('open_place')}
					hint="Elle s'ouvre par-dessus le plan, avec son image, son nom et ses informations."
				>
					Afficher la fiche du {placeName}
				</Choice>

				<Choice selected={!isMixed && commonAction === 'open_url'} onClick={() => setAction('open_url')}>
					Ouvrir une page web
				</Choice>

				{/* Le libellé calque celui du point d'intérêt (« Afficher… ») : dans les deux cas le visiteur
				    reste sur le plan, rien ne s'ouvre à côté. L'explication dit d'abord ce qui se passe à
				    l'écran, puis à quoi ça sert — « une autre carte » seul ne le laisse pas deviner, alors
				    qu'un escalier ou une porte se reconnaissent tout de suite dans son propre projet. */}
				{withMapNestingGate(
					<Choice
						selected={!isMixed && commonAction === 'open_map'}
						onClick={() => setAction('open_map')}
						hint="Le plan est remplacé par la carte choisie : un escalier vers un autre étage, une porte vers un autre bâtiment…"
					>
						Afficher une autre carte
					</Choice>,
				)}

				<Choice selected={!isMixed && !commonAction} onClick={() => setAction(undefined)}>
					Aucune action
				</Choice>
			</View>

			{/* Changer d'action défait la liaison (voir `setAction`) : on le dit avant le clic, plutôt que
			    de laisser la fiche disparaître sans prévenir. */}
			{!!linkedShape && commonAction === 'open_place' && (
				<View size={12} color={tokens.text.muted}>
					Choisir une autre action dissocie la forme de « {placeLabel(linkedShape) || `ce ${placeName}`} » et
					retire son nom et son image de la forme.
				</View>
			)}

			{!isMixed && commonAction === 'open_place' && (
				currentEditorStore.isMultiSelection
					? <AreaPlaceSection />
					: !shapes[0]?.entity && (
						<View size={13} color={tokens.text.muted}>
							Associez d'abord la forme à un {placeName}, en haut du panneau.
						</View>
					)
			)}
			{!isMixed && commonAction === 'open_url' && <AreaUrlSection />}
			{!isMixed && commonAction === 'open_map' && <AreaMapSection />}
		</View>
	);
});

export default AreaActionSection;
